import React, { useEffect, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { FaRegListAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import RequestService from "../../services/RequestService";
import MotivoJustificativa from "../../enums/MotivoJustificativa";
import ResponsavelJustificativa from "../../enums/ResponsavelJustificativa";
import FaixaJustificativa from "../../enums/FaixaJustificativa";
import RequestDetailsModal from "./RequestDetailsModal";
import EstoqueCodSapPanel from "./EstoqueCodSapPanel";

// O que dá para fazer com a justificativa, conforme o estado do workflow.
// A regra está aqui, e não em quem chama, para a tela e o modal nunca
// discordarem sobre o que é editável — o backend recusa de qualquer forma.
const MODOS = {
    justificar: { titulo: "Justificativa", acao: "Salvar", sub: "Preencha a justificativa para essa solicitação." },
    editar: { titulo: "Corrigir justificativa", acao: "Salvar correção", sub: "A Vivo ainda não avaliou — dá para corrigir o que foi preenchido." },
    replicar: { titulo: "Réplica", acao: "Enviar réplica", sub: "Responda ao parecer da Vivo. A réplica é permitida uma única vez." },
    ver: { titulo: "Justificativa (somente leitura)", acao: null, sub: "A Vivo já avaliou — a justificativa não pode mais ser alterada." },
};

function modoDaRequest(request) {
    const status = request?.justificativaInfo?.status || "JUSTIFICAR";
    const jaReplicou = (request?.justificativaInfo?.historico || []).some((h) => h.acao === "REPLICOU");

    if (status === "JUSTIFICAR") return "justificar";
    if (status === "AGUARDANDO_APROVACAO") return "editar";
    if ((status === "RECUSADO" || status === "EM_REPLICA") && !jaReplicou) return "replicar";
    return "ver";
}

/**
 * Modal para preencher / atualizar a justificativa de uma Request (lado Amerinode).
 *
 * Ao salvar, chama o endpoint de workflow `justificarRequest`, que persiste o
 * comentário (justificativa), o motivo e o flag somenteFaturamento, registra o
 * histórico (JUSTIFICOU / EDITOU / REPLICOU) e deixa o status em
 * AGUARDANDO_APROVACAO (a bola fica com a Vivo).
 *
 * Props:
 *  - isOpen (bool)        — controla visibilidade
 *  - setIsOpen (fn)       — fecha o modal
 *  - request (object)     — Request alvo (_id, idRequest, justificativa, justificativaInfo)
 *  - onSaved (fn)         — callback após salvar com sucesso (recarrega a lista)
 *  - onClosed (fn)        — callback ao fechar sem salvar. Abrir o modal pode
 *                           persistir dados no backend (ex.: write-through da
 *                           faixa calculada), então a tela de origem precisa
 *                           recarregar mesmo quando o usuário só consulta.
 */
export default function JustificarModal({ isOpen, setIsOpen, request, onSaved, onClosed }) {
    const [texto, setTexto] = useState("");
    const [motivo, setMotivo] = useState("");
    const [responsavel, setResponsavel] = useState("");
    const [faixa, setFaixa] = useState("");
    const [somenteFaturamento, setSomenteFaturamento] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Faixa calculada pelo backend (drsAtender x drsOriginal) — aviso + pré-seleção.
    const [faixaCalculada, setFaixaCalculada] = useState(null);
    // Controla a abertura do modal de detalhes do chamado (RequestDetailsModal) por cima.
    const [showDetalhes, setShowDetalhes] = useState(false);

    const modo = modoDaRequest(request);
    const rotulos = MODOS[modo];
    const somenteLeitura = modo === "ver";

    // Sincroniza os campos com os valores atuais da Request a cada abertura e
    // busca a faixa calculada para pré-selecionar / exibir o aviso no topo.
    useEffect(() => {
        if (!isOpen) return;

        // O dropdown edita a faixa FINAL (a que vale). Cai na original quando ainda não houve troca.
        const savedFaixa = request?.faixa_final || request?.faixa || "";
        setTexto(request?.justificativa || "");
        setMotivo(request?.justificativaInfo?.motivo || "");
        setResponsavel(request?.justificativaInfo?.responsavel || "");
        setFaixa(savedFaixa);
        setSomenteFaturamento(request?.justificativaInfo?.somenteFaturamento || false);
        setFaixaCalculada(null);

        if (!request?._id) return;
        let alive = true;
        (async () => {
            try {
                const info = await RequestService.getFaixaJustificativa(request._id);
                if (!alive) return;
                setFaixaCalculada(info);
                // Pré-seleciona só quando ainda não há faixa salva (preserva a escolha anterior).
                if (!savedFaixa && (info?.faixaFinal || info?.faixa)) {
                    setFaixa(info.faixaFinal || info.faixa);
                }
            } catch (e) {
                // silencioso — sem faixa calculada (ex.: chamado sem DRS atendido/indicado)
            }
        })();

        return () => { alive = false; };
    }, [isOpen, request]);

    // Rótulo amigável da faixa calculada.
    const faixaLabel = (cod) => (cod === "99" ? "Dentro de Área (99)" : `Faixa ${cod}`);

    // notificar=false quando o fechamento vem logo após um save bem-sucedido —
    // nesse caso o onSaved já recarregou a tela de origem.
    const fecharModal = (notificar) => {
        setIsOpen(false);
        setTexto("");
        setMotivo("");
        setResponsavel("");
        setFaixa("");
        setSomenteFaturamento(false);
        setFaixaCalculada(null);
        setShowDetalhes(false);
        if (notificar && onClosed) onClosed();
    };

    const handleClose = () => fecharModal(true);

    const handleSave = async () => {
        const value = texto.trim();
        if (!value) {
            toast.error("Digite uma justificativa antes de salvar.");
            return;
        }
        if (!motivo) {
            toast.error("Selecione um motivo.");
            return;
        }
        setIsSaving(true);
        try {
            const resp = await RequestService.justificarRequest(request._id, {
                justificativa: value,
                motivo,
                responsavel,
                faixa,
                somenteFaturamento,
            });
            toast.success(resp?.message || "Justificativa enviada para aprovação!");
            if (onSaved) onSaved();
            fecharModal(false); // onSaved já recarregou — evita reload duplicado
        } catch (error) {
            toast.error(error?.message || "Erro ao salvar justificativa.");
        }
        setIsSaving(false);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50"
            onClick={handleClose}
        >
            <div
                className="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={handleClose}
                    className="absolute -top-7 -right-7 rounded-full p-1"
                >
                    <div className="w-12 h-12 flex bg-gray-200 justify-center items-center rounded-full">
                        <IoIosClose color="black" size={32} />
                    </div>
                </button>

                <div className="flex flex-col px-4 pt-4 pb-3 gap-2 bg-gray-800 text-white rounded-t-lg">
                    <div className="text-xl font-bold">
                        {rotulos.titulo} — # {request?.idRequest}
                    </div>
                    <div className="text-xs text-gray-300">
                        {rotulos.sub}
                    </div>
                </div>

                <div className="flex flex-col gap-3 p-4 text-sm">
                    {/* Parecer da Vivo — é a razão da réplica e a explicação de um
                        chamado travado. Sem ele aqui, só aparecia na grade. */}
                    {request?.justificativaInfo?.observacaoVivo?.trim() && (
                        <div className="p-3 rounded-md border border-purple-200 bg-purple-50">
                            <span className="block text-[11px] font-bold text-purple-700 uppercase">Parecer da Vivo</span>
                            <span className="block text-gray-800 whitespace-pre-wrap">
                                {request.justificativaInfo.observacaoVivo}
                            </span>
                        </div>
                    )}

                    {/* Aviso: faixa calculada pelo sistema (drsAtender x drsOriginal) */}
                    {faixaCalculada && (
                        faixaCalculada.faixa ? (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 p-3 rounded-md border border-sky-200 bg-sky-50 text-sky-800">
                                <span className="font-semibold">Faixa calculada:</span>
                                <span className="font-bold">{faixaLabel(faixaCalculada.faixa)}</span>
                                {faixaCalculada.drsAtender && faixaCalculada.drsOriginal && (
                                    <span className="text-xs text-sky-600">
                                        · DRS {faixaCalculada.drsAtender} (acionado) × {faixaCalculada.drsOriginal} (indicado)
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className="p-3 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-500 text-xs">
                                Faixa não pôde ser calculada automaticamente (DRS atendido/indicado ausente). Selecione manualmente, se necessário.
                            </div>
                        )
                    )}

                    {/* Movimentações 36 meses + saldo do dia — só quando o DRS atendido
                        difere do indicado. Permite comparar o que deveria haver em
                        estoque com o que de fato havia no dia da abertura. */}
                    {/* Estoque do código SAP: movimentações 36m, saldo do dia e faixa ideal.
                        Componente compartilhado com o ValidarJustificativaModal. */}
                    <EstoqueCodSapPanel request={request} />

                    {/* Linha superior: Motivo | Responsável | Faixa */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="font-semibold text-gray-700">Motivo:</label>
                            <select
                                value={motivo}
                                disabled={somenteLeitura}
                                onChange={(e) => setMotivo(e.target.value)}
                                className="w-full rounded-md px-3 py-2 text-gray-900 shadow-sm border border-zinc-400 outline-none ring-0 disabled:bg-gray-100 disabled:text-gray-500"
                            >
                                <option value="" disabled>Selecione um motivo</option>
                                {MotivoJustificativa.map((m) => (
                                    <option key={m.cod} value={m.cod}>{m.descricao}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="font-semibold text-gray-700">Responsável:</label>
                            <select
                                value={responsavel}
                                disabled={somenteLeitura}
                                onChange={(e) => setResponsavel(e.target.value)}
                                className="w-full rounded-md px-3 py-2 text-gray-900 shadow-sm border border-zinc-400 outline-none ring-0 disabled:bg-gray-100 disabled:text-gray-500"
                            >
                                <option value="">Selecione</option>
                                {ResponsavelJustificativa.map((r) => (
                                    <option key={r.cod} value={r.cod}>{r.descricao}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="font-semibold text-gray-700">Faixa:</label>
                            <select
                                value={faixa}
                                disabled={somenteLeitura}
                                onChange={(e) => setFaixa(e.target.value)}
                                className="w-full rounded-md px-3 py-2 text-gray-900 shadow-sm border border-zinc-400 outline-none ring-0 disabled:bg-gray-100 disabled:text-gray-500"
                            >
                                <option value="">Selecione</option>
                                {FaixaJustificativa.map((f) => (
                                    <option key={f.cod} value={f.cod}>{f.descricao}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Comentário */}
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-gray-700">Comentário:</label>
                        <textarea
                            value={texto}
                            disabled={somenteLeitura}
                            onChange={(e) => setTexto(e.target.value)}
                            rows={5}
                            placeholder="Descreva o motivo do atraso ou da situação..."
                            className="w-full rounded-md px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 border border-zinc-400 outline-none ring-0 resize-none disabled:bg-gray-100 disabled:text-gray-500"
                        />
                    </div>

                    <label className="flex items-start gap-3 p-3 rounded-md border border-amber-200 bg-amber-50 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={somenteFaturamento}
                            disabled={somenteLeitura}
                            onChange={(e) => setSomenteFaturamento(e.target.checked)}
                            className="mt-0.5 accent-amber-600 cursor-pointer"
                        />
                        <span>
                            <span className="block font-semibold text-amber-800">Somente Faturamento</span>
                            <span className="block text-xs text-amber-700">
                                Marque para que o chamado conste apenas no faturamento e <strong>não seja contabilizado no SLA</strong>.
                            </span>
                        </span>
                    </label>

                    {/* Botão que abre os detalhes do chamado (RequestDetailsModal por cima) */}
                    <button
                        type="button"
                        onClick={() => setShowDetalhes(true)}
                        className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50"
                    >
                        <FaRegListAlt className="text-slate-500" />
                        Detalhes do Chamado
                    </button>

                    <div className="flex justify-end gap-2 mt-2">
                        <button
                            onClick={handleClose}
                            disabled={isSaving}
                            className="bg-zinc-500 hover:bg-zinc-600 text-white px-4 py-1 rounded-2xl disabled:opacity-50"
                        >
                            {somenteLeitura ? "Fechar" : "Cancelar"}
                        </button>
                        {!somenteLeitura && (
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded-2xl disabled:opacity-50"
                            >
                                {isSaving ? "Salvando..." : rotulos.acao}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Detalhes do chamado — modal por cima (z-10 dentro do z-50 do justificar).
                O stopPropagation evita que cliques no backdrop dos detalhes borbulhem
                até o onClick do root do JustificarModal e fechem os dois. */}
            {showDetalhes && request?._id && (
                <div onClick={(e) => e.stopPropagation()}>
                    <RequestDetailsModal
                        isModalOpen={showDetalhes}
                        setModalOpen={() => setShowDetalhes(false)}
                        requestId={request._id}
                    />
                </div>
            )}
        </div>
    );
}
